import React, {useEffect} from 'react'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {useTemplate} from 'Event/TemplateProvider'
import {components} from 'Event/template'
import {FieldErrors, useValidatedForm} from 'lib/form'
import {useState} from 'react'
import {UseFormMethods} from 'react-hook-form'
import {client, ValidationError} from 'lib/ui/api-client'
import {Attendee} from 'Event/attendee'
import {Redirect} from 'react-router'
import {eventRoutes} from 'Event/Routes'
import {useIsAttendee} from 'Event/auth'
import Scripts, {SIGN_UP} from 'Event/Scripts'

export interface SignUpData {
  email: string
  first_name: string
  last_name: string
  phone_number: string
  groups?: Record<string, string>
}

export interface SignUpProps {
  onSubmit: () => void
  register: UseFormMethods['register']
  control: UseFormMethods['control']
  submitting: boolean
  errors: FieldErrors<SignUpData>
  responseError: ValidationError<SignUpData>
}

export default function SignUp() {
  const {event} = useEvent()
  const [attendee, setAttendee] = useState<Attendee | null>(null)

  const isAttendee = useIsAttendee()

  // Auto-login once an attendee has been created.
  useEffect(() => {
    if (!attendee) {
      return
    }

    window.location.href = attendee.login_url
  }, [attendee])

  const form = useSignUpForm(setAttendee)

  const {name} = useTemplate()
  const Component = components[name].SignUp.index

  const shouldRedirectToLogin = isAttendee && !event.sign_up_enabled
  if (shouldRedirectToLogin) {
    return <Redirect to={eventRoutes.login} />
  }

  return (
    <>
      <Scripts page={SIGN_UP} />
      <Component {...form} />
    </>
  )
}

export function useSignUpForm(
  setAttendee: (attendee: Attendee) => void,
): SignUpProps {
  const {event} = useEvent()
  const {signUp: signUpConfig} = useTemplate()
  const additionalFields = signUpConfig.additionalFields ?? []
  const url = api(`/events/${event.id}/attendees/sign_up`)

  const {
    control,
    register,
    handleSubmit,
    errors,
    setResponseError,
    clearErrors,
    responseError,
  } = useValidatedForm<SignUpData>()

  const [submitting, setSubmitting] = useState(false)

  const signUp = (body: SignUpData) => client.post<Attendee>(url, body)

  const submit = (data: Record<string, any>) => {
    const {email, first_name, last_name, phone_number} = data
    const groups: Record<string, string> = {}
    for (const field of additionalFields) {
      const value = data[field.groupKey]
      if (value !== undefined && value !== '') {
        groups[field.groupKey] = value
      }
    }

    const body: SignUpData = {email, first_name, last_name, phone_number}
    if (Object.keys(groups).length > 0) {
      body.groups = groups
    }

    clearErrors()
    setSubmitting(true)
    signUp(body)
      .then(setAttendee)
      .catch(setResponseError)
      .finally(() => setSubmitting(false))
  }

  return {
    submitting,
    control,
    register,
    onSubmit: handleSubmit(submit),
    errors,
    responseError,
  }
}
